// src/data/supportPathways.js
//
// HELP & SUPPORT PATHWAYS — what a member can do next when a phone number is
// not the thing they need. A pathway set reinforces talking to someone they
// trust, gives them words to start with, and tells them what happens after.
// It also carries the coach-facing guidance for when a player comes to a
// coach ("listen, respond, connect — not counsel").
//
// A set is a DATA MODULE keyed by id; an org opts in with
//   organizations/{id}.supportPathwaysId = 'hockey_alberta'
// (Super Admin → Organization editor). No org field = no pathway sections; the
// Talk-to-Someone modal then reads exactly as it did before.
//
// Rendered by components/dashboard/SupportPathways.jsx (member sections, in the
// Talk-to-Someone modal) and components/coach/CoachPathwaysCard.jsx (the coach
// block, on the team dashboard). A new client's set is a new entry here — never
// a component fork.
//
// The first set is Hockey Alberta's "Player Wellness Pilot – Help & Support
// Pathways" (2026-09-26), lightly edited for the screen.

import { isYouthMember } from './crisisResources';

const HOCKEY_ESCALATION_ROUTES = [
    { label: 'Coach or bench staff', text: 'where the concern can appropriately be supported or addressed at the team level.' },
    { label: 'Minor Hockey Association leadership', text: 'where the concern needs association awareness, review or action.' },
    // The ITP's reporting page (Carlia, 2026-10-27). A route with an href renders its label as a link.
    { label: 'Hockey Canada Independent Third Party (ITP)', text: 'where the concern involves serious maltreatment or otherwise falls within the ITP reporting process.', href: 'https://sportdispute-hockey.com/' }
];

// Illustrative routes for the community demo set: a real community writes its
// own people, programs and reporting routes here.
const COMMUNITY_ROUTES = [
    { label: 'Program or community wellness staff', text: 'where the concern can be supported within the program or the health centre.' },
    { label: 'Community leadership or the designated safety contact', text: 'where the concern needs leadership awareness, review or action.' },
    { label: 'A crisis line or emergency services', text: 'Hope for Wellness (1-855-242-3310), 9-8-8, or 911 when someone is in danger.' }
];

export const SUPPORT_PATHWAYS = {
    hockey_alberta: {
        id: 'hockey_alberta',
        label: 'Hockey Alberta — Help & Support Pathways',
        member: {
            // Under-18 players (kid and teen tiers).
            youth: [
                {
                    id: 'ask', icon: 'MessageCircle',
                    title: 'Ask someone for help',
                    lead: "You don't have to know exactly what's wrong before you ask for help.",
                    intro: "If something doesn't feel right, you've been struggling, or you simply want someone to talk to, reach out to someone you trust. That could be:",
                    people: [
                        'a parent or caregiver',
                        'another trusted adult, family or community member',
                        'a coach, manager or other team staff member',
                        'a teacher or school counsellor',
                        'a healthcare or mental health professional'
                    ],
                    startersTitle: 'Not sure what to say? Start small.',
                    starters: [
                        'Can I talk to you about something?',
                        "I haven't really been feeling like myself lately.",
                        "I've been having a hard time sleeping and I don't know why.",
                        'Hockey has been stressing me out more than usual.',
                        "Something happened at hockey and I don't know what to do about it.",
                        "I'm not really sure how to explain it, but I think I need some help.",
                        'Can you help me figure out who I should talk to?'
                    ],
                    outro: [
                        "You don't need to have the right words, know what kind of help you need, or wait until something becomes a crisis before talking to someone.",
                        "If the first person you talk to can't give you the help you need, keep asking. Another trusted adult or support line can help you find the right next step."
                    ]
                },
                {
                    id: 'incident', icon: 'ShieldAlert',
                    title: 'Something happened in hockey',
                    lead: "If something happened in hockey that made you feel unsafe, uncomfortable, targeted or worried, or you're worried about something that happened to someone else, you don't need to know what policy applies or where a report should go.",
                    intro: 'Your job is to tell an adult you trust. You could tell:',
                    people: [
                        'your parent or caregiver',
                        "your coach or another member of your team's bench staff",
                        'another trusted adult who can help you reach the right person'
                    ],
                    outro: [
                        "If you tell someone and you don't feel like you're getting the help you need, keep telling. Talk to another trusted adult until someone helps you take the next step.",
                        "It is the adults' responsibility to figure out what happens next. Not yours.",
                        'A hit to the head is different: it is an injury, not a report. Tell your coach or the safety person right away, and you are done for the day. The Concussions page under Player Safety has what happens next.'
                    ]
                }
            ],
            // Adults on the org: parents, caregivers, team staff.
            adult: [
                {
                    id: 'caregivers', icon: 'Users',
                    title: 'When a young person brings a concern to you',
                    lead: 'Listen, and help them get to the right next step. Depending on the concern that may be:',
                    routes: HOCKEY_ESCALATION_ROUTES,
                    outro: [
                        'The young person should not have to work out jurisdiction, find the right policy or navigate a complaint process. Their job is to tell someone. The adults around them are responsible for the support and the escalation.',
                        'Support resources can be used alongside any hockey reporting or complaint process.'
                    ]
                }
            ]
        },
        coach: {
            title: 'A player asked to talk. Now what?',
            intro: 'Players may come to you because they trust you, or because something in their life or hockey environment is affecting them. Your role is not to diagnose, counsel or fix the problem. It is to listen, respond well and help them reach the right support.',
            listen: {
                title: 'Start by listening',
                text: 'Give the player your attention and let them share what they are comfortable sharing. Responses that help:',
                lines: [
                    'Thanks for telling me.',
                    "I'm glad you came to talk to me.",
                    "You don't have to explain everything at once.",
                    'What would be helpful for you right now?',
                    'Is there an adult at home, or someone else you trust, who knows about this?',
                    "Let's figure out who can help you with this."
                ]
            },
            avoid: {
                title: 'Things to avoid',
                items: [
                    "Don't promise to keep everything secret.",
                    "Don't try to diagnose what the player is experiencing.",
                    'Don\'t minimize it with "everyone gets stressed" or "you\'ll be fine".',
                    "Don't push for details that aren't needed.",
                    "Don't take on the role of counsellor or mental health professional."
                ],
                note: "A concern doesn't have to be a crisis to deserve support. A player struggling with sleep, stress, anxiety, confidence, relationships or something happening in hockey may simply need someone to listen and help them find the next step."
            },
            next: {
                title: 'Help them find their next person',
                text: "When there isn't an immediate safety concern, help the player think about who else they could talk to:",
                ask: 'Who else would you feel comfortable talking to about this?',
                note: 'That may be a parent or caregiver, another trusted adult, school support, a healthcare professional or one of the lines below.'
            },
            urgent: {
                title: 'When there is an immediate safety concern',
                text: 'If a player tells you they may hurt themselves or someone else, is in immediate danger, or you believe there is an urgent safety concern:',
                items: [
                    'Stay with the player and get appropriate adult or emergency support.',
                    'Call 911 for an immediate emergency.',
                    'Call or text 9-8-8 for suicide crisis support.',
                    'For a minor, involve their parent or caregiver where appropriate and safe to do so.',
                    'Follow your team, association and Hockey Alberta safety and reporting requirements.'
                ]
            },
            escalation: {
                title: 'When something has happened in hockey',
                text: 'The player is not responsible for working out where a concern goes. Depending on the concern, the next step may be:',
                routes: HOCKEY_ESCALATION_ROUTES
            }
        }
    },

    // The community demo set (2026-10-13): the Hockey Alberta shape with the
    // hockey removed, for a community wellness hub. Every line is a placeholder
    // for what a community writes with its partners; nothing here is any
    // community's real guidance.
    community_demo: {
        id: 'community_demo',
        label: 'Community demo — Help & Support Pathways (illustrative)',
        member: {
            youth: [
                {
                    id: 'ask', icon: 'MessageCircle',
                    title: 'Ask someone for help',
                    lead: "You don't have to know exactly what's wrong before you ask for help.",
                    intro: "If something doesn't feel right, you've been struggling, or you just want someone to talk to, reach out to someone you trust. That could be:",
                    people: [
                        'a parent, caregiver or someone in your family',
                        'an Elder or another trusted adult in your community',
                        'a teacher or school counsellor',
                        'a community health or wellness worker',
                        'a healthcare or mental health professional'
                    ],
                    startersTitle: 'Not sure what to say? Start small.',
                    starters: [
                        'Can I talk to you about something?',
                        "I haven't really been feeling like myself lately.",
                        "I've been having a hard time sleeping and I don't know why.",
                        'Things at home have been a lot lately.',
                        "Something happened and I don't know what to do about it.",
                        "I'm not really sure how to explain it, but I think I need some help.",
                        'Can you help me figure out who I should talk to?'
                    ],
                    outro: [
                        "You don't need the right words, or to know what kind of help you need, or to wait until it becomes a crisis before talking to someone.",
                        "If the first person you talk to can't help, keep asking. Another trusted adult, Kids Help Phone or the Hope for Wellness line can help you find the next step."
                    ]
                },
                {
                    id: 'incident', icon: 'ShieldAlert',
                    title: "Something happened and you're not sure what to do",
                    lead: "If something happened that made you feel unsafe, uncomfortable, targeted or worried, or you're worried about something that happened to someone else, you don't need to know what to call it or who is supposed to deal with it.",
                    intro: 'Your job is to tell an adult you trust. You could tell:',
                    people: [
                        'your parent, caregiver or someone in your family',
                        'an Elder, a teacher or a wellness worker',
                        'another trusted adult who can help you reach the right person'
                    ],
                    outro: [
                        "If you tell someone and you don't feel like you're getting the help you need, keep telling. Talk to another trusted adult until someone helps you take the next step.",
                        "It is the adults' responsibility to figure out what happens next. Not yours."
                    ]
                }
            ],
            adult: [
                {
                    id: 'caregivers', icon: 'Users',
                    title: 'When a young person brings a concern to you',
                    lead: 'Listen, and help them get to the right next step. Depending on the concern that may be:',
                    routes: COMMUNITY_ROUTES,
                    outro: [
                        'The young person should not have to work out which service, which program or which process applies. Their job is to tell someone. The adults around them carry the support and the next step.',
                        'Illustrative: a community hub carries its own people, programs and reporting routes here, written with the community.'
                    ]
                }
            ]
        },
        coach: {
            title: 'Someone asked to talk. Now what?',
            intro: 'People come to program staff because they trust them. Your role is not to diagnose, counsel or fix the problem. It is to listen, respond well and help them reach the right support.',
            listen: {
                title: 'Start by listening',
                text: 'Give them your attention and let them share what they are comfortable sharing. Responses that help:',
                lines: [
                    'Thanks for telling me.',
                    "I'm glad you came to talk to me.",
                    "You don't have to explain everything at once.",
                    'What would be helpful for you right now?',
                    'Is there someone at home, or someone else you trust, who knows about this?',
                    "Let's figure out who can help you with this."
                ]
            },
            avoid: {
                title: 'Things to avoid',
                items: [
                    "Don't promise to keep everything secret.",
                    "Don't try to diagnose what they are experiencing.",
                    'Don\'t minimize it with "everyone goes through this".',
                    "Don't push for details that aren't needed.",
                    "Don't take on the role of counsellor or mental health professional."
                ],
                note: "A concern doesn't have to be a crisis to deserve support. Someone struggling with sleep, stress, family, money or something that happened may simply need someone to listen and help them find the next step."
            },
            next: {
                title: 'Help them find their next person',
                text: "When there isn't an immediate safety concern, help them think about who else they could talk to:",
                ask: 'Who else would you feel comfortable talking to about this?',
                note: 'That may be family, an Elder or another trusted person, the health centre, a counsellor, or one of the lines below.'
            },
            urgent: {
                title: 'When there is an immediate safety concern',
                text: 'If someone tells you they may hurt themselves or someone else, is in immediate danger, or you believe there is an urgent safety concern:',
                items: [
                    'Stay with them and get appropriate adult or emergency support.',
                    'Call 911 for an immediate emergency.',
                    'Call or text 9-8-8 for suicide crisis support; Hope for Wellness (1-855-242-3310) for culturally grounded support, 24/7.',
                    'For a minor, involve their parent or caregiver where appropriate and safe to do so.',
                    "Follow your program's and community's safety and reporting requirements."
                ]
            },
            escalation: {
                title: 'When something has happened',
                text: 'The person is not responsible for working out where a concern goes. Depending on the concern, the next step may be:',
                routes: COMMUNITY_ROUTES
            }
        }
    }
};

// First-responder services (2026-10-16): the same shape for police, fire,
// paramedic, dispatch and corrections. Written for a demo; a service replaces
// the people and routes with its own (peer support team, member and family
// assistance, chaplaincy, reintegration, its health services). Adults only:
// a service's members are adults, and a family org handles their kids.
const RESPONDER_ROUTES = [
    { label: 'Peer support', text: 'a trained peer with lived experience, for the first conversation and the ones after it.' },
    { label: 'Member and family assistance, or a psychologist', text: 'confidential short-term counselling, and a path to clinical care when it is needed.' },
    { label: "The service's health and reintegration programs", text: 'where a call or an exposure needs a planned return, not a quiet one.' },
    { label: 'A crisis line or emergency services', text: '9-8-8 any time; 911 when someone is in danger.' }
];
SUPPORT_PATHWAYS.first_responder = {
    id: 'first_responder',
    label: 'First responders — Help & Support Pathways',
    member: {
        youth: [],
        adult: [
            {
                id: 'ask', icon: 'MessageCircle',
                title: 'Ask someone',
                lead: "You don't have to know what it is before you say it out loud.",
                intro: "If a call is still with you, you're not sleeping, you're fine on shift and not fine at home, or you just want someone to talk to, reach out to someone you trust. That could be:",
                people: [
                    'a partner or someone on your watch you trust',
                    'a peer supporter: someone trained, with lived experience, who is not your supervisor',
                    'your supervisor, if that feels right',
                    'the chaplain or wellness contact for your detachment or station',
                    'your doctor, a psychologist, or the member and family assistance line, any time'
                ],
                startersTitle: 'Not sure how to start? Keep it short.',
                starters: [
                    'Can I run something by you?',
                    'That call is still with me.',
                    "I haven't slept properly in two weeks.",
                    "I'm fine on shift and not fine at home.",
                    "I don't know if this is a thing, but I think I need to talk to someone.",
                    'Who did you talk to, when it was you?'
                ],
                outro: [
                    "Talking to a peer is not a report, not a file entry and not a fitness-for-duty question. It is what the people who stay in this job longest do early.",
                    "If the first person can't help, ask the next one. A peer supporter can also walk you to the right door."
                ]
            },
            {
                id: 'incident', icon: 'ShieldAlert',
                title: 'After a hard call',
                lead: "It is normal for a call to sit with you for days: sleep, appetite, patience and focus shift. That is a load, not a verdict.",
                intro: 'What helps in the first week, in order:',
                people: [
                    'sleep and food first; the body settles before the mind does',
                    'say it to one person: a partner, a peer supporter, someone who was there',
                    'take the debrief if one is offered; ask for one if it is not',
                    "use the service's post-incident support while it is close, not later"
                ],
                outro: [
                    'If it is still sitting with you after a few weeks, or it is getting louder instead of quieter, that is the moment for a psychologist or the assistance line. Early is normal.',
                    'Nothing you say in this app reaches your supervisor, your detachment or the service.'
                ]
            }
        ]
    },
    coach: {
        title: 'A member asked to talk. Now what?',
        intro: 'Members come to you because they trust you, or because the job is sitting on them. Your role is not to diagnose, counsel or decide anything about their duty status. It is to listen, respond well and help them reach the right support.',
        listen: {
            title: 'Start by listening',
            text: 'Give them your attention and let them say what they want to say. Responses that help:',
            lines: [
                'Thanks for telling me.',
                "I'm glad you came to me.",
                "You don't have to explain all of it.",
                'What would help right now?',
                'Who else knows?',
                "Let's figure out who can help with this."
            ]
        },
        avoid: {
            title: 'Things to avoid',
            items: [
                "Don't promise to keep everything secret.",
                "Don't try to diagnose what they are experiencing.",
                'Don\'t minimize it with "everyone has those calls" or "you\'ll be fine".',
                "Don't push for details that aren't needed.",
                "Don't turn a conversation into a fitness-for-duty judgment."
            ],
            note: "A concern doesn't have to be a crisis to deserve support. Sleep, home, a case that won't close, court on rest days, a call that stuck: any of it can be the thing."
        },
        next: {
            title: 'Help them find their next person',
            text: "When there isn't an immediate safety concern, help them think about who else they could talk to:",
            ask: 'Who would you be comfortable talking to about this?',
            note: 'That may be a peer supporter, the assistance line, a psychologist, the chaplain, or one of the lines below.'
        },
        urgent: {
            title: 'When there is an immediate safety concern',
            text: 'If a member tells you they may hurt themselves or someone else, or you believe there is an urgent safety concern:',
            items: [
                'Stay with them and get appropriate support to them.',
                'Call 911 for an immediate emergency.',
                'Call or text 9-8-8 for suicide crisis support.',
                "Follow your service's critical-incident and member-safety procedures."
            ]
        },
        escalation: {
            title: 'Where it can go next',
            text: 'The member is not responsible for working out which program applies. Depending on the concern, the next step may be:',
            routes: RESPONDER_ROUTES
        }
    }
};

// For the org editor dropdown.
export const SUPPORT_PATHWAY_OPTIONS = [
    { id: '', label: 'None' },
    ...Object.values(SUPPORT_PATHWAYS).map(p => ({ id: p.id, label: p.label }))
];

export const resolveSupportPathways = (orgData) =>
    (orgData?.supportPathwaysId && SUPPORT_PATHWAYS[orgData.supportPathwaysId]) || null;

// The member-facing sections for this member, by age.
export const memberPathwaysFor = (pathways, { memberType, ageTier } = {}) => {
    if (!pathways?.member) return [];
    return (isYouthMember({ memberType, ageTier }) ? pathways.member.youth : pathways.member.adult) || [];
};
